import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";

import NotificationBlock from "~/components/notificationBlock/NotificationBlock";
import { useManualPoller } from "~/components/siteSpeed/hooks";
import ButtonWithSpinner from "~/components/ui/button/Button";
import CopyButton from "~/components/ui/copyButton/CopyButton";

import { DnsConfig } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import { useWizard } from "../../../contexts/WizardContext";
import FormLabel from "../../login/formLabel/FormLabel";
import Input from "../../ui/input/Input";
import styles from "./Wizzard.module.scss";

interface VerificationProps {
  data?: DnsConfig | undefined;
}

interface RouteParams {
  domainId: string;
  [key: string]: string | undefined;
}

const POLLING_INTERVAL = 5000;

const Verification = ({ data }: VerificationProps) => {
  const { updateDomain, domain } = useWizard();
  const { domainId } = useParams<RouteParams>();

  const { mutate: checkStatus } = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.domains.statusDetail(Number(domainId));

      return data?.domain;
    },
    onSuccess: async (domain) => {
      if (domain?.status?.verification) {
        enqueueSnackbar("Domain verified successfully!", {
          variant: "success",
        });
        updateDomain(domain, { navigateToNextStep: true });
        stopPolling();
      }
    },
    onError: () => {
      stopPolling();
      enqueueSnackbar("Failed to check verification status", {
        variant: "error",
        persist: true,
      });
    },
  });

  const {
    start: startPolling,
    stop: stopPolling,
    isPolling,
  } = useManualPoller(async () => {
    await checkStatus();
  }, POLLING_INTERVAL);

  const handleContinue = () => {
    startPolling();
  };

  const extractChallengeHost = (hostValue: string): string => {
    const parts = String(hostValue).split(".");
    if (parts.length <= 1) {
      return String(hostValue);
    }

    return parts[0] || hostValue;
  };

  return (
    // @ts-expect-error TS2339 non-existing className
    <div className={styles.stepContent}>
      <div className={styles.stepContentTitle}>Verification</div>
      {domain?.distinct === false && (
        <NotificationBlock type="info" className={styles.verificationAlert}>
          <div>
            <strong>
              This domain is already verified under another account
            </strong>
          </div>
          <div>
            Verification will not succeed while this domain is verified under
            another account. Please remove the verification from the other
            domain first, or contact support for assistance.
          </div>
        </NotificationBlock>
      )}
      {/*
       // @ts-expect-error TS2339 non-existing className */}
      <div className={styles.stepWrapper}>
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles.stepContentSection}>
          <ol className={styles.stepContentList}>
            <li className={styles.stepContentListItem}>
              Go to your domain registrar, log into your account, and find the
              DNS records.
            </li>
            <li className={styles.stepContentListItem}>
              Add a new TXT record in your DNS configuration using the following
              values.
            </li>
          </ol>

          {data?.map((item, index) => {
            return (
              <div className={styles.stepContentCodeFlex} key={index}>
                {/*
                 // @ts-expect-error TS2551 non-existing className */}
                <div className={styles.stepContentCode}>
                  <FormLabel label="DNS record type" />
                  <div className={styles.stepContentCodeInput}>
                    <Input
                      readOnly
                      value={String(item[2])}
                      ownProps={{ noErrors: true }}
                    />
                    <CopyButton value={String(item[2])} />
                  </div>
                </div>
                {/*
                 // @ts-expect-error TS2551 non-existing className */}
                <div className={styles.stepContentCode}>
                  <FormLabel label="Host" />
                  <div className={styles.stepContentCodeInput}>
                    <Input
                      readOnly
                      value={extractChallengeHost(String(item[0]))}
                      ownProps={{ noErrors: true }}
                    />
                    <CopyButton value={extractChallengeHost(String(item[0]))} />
                  </div>
                </div>
                {/*
                 // @ts-expect-error TS2551 non-existing className */}
                <div className={styles.stepContentCode}>
                  <FormLabel label="Value" />
                  <div className={styles.stepContentCodeInput}>
                    <Input
                      readOnly
                      value={String(item[3])}
                      ownProps={{ noErrors: true }}
                    />
                    <CopyButton value={String(item[3])} />
                  </div>
                </div>
                {/*
                 // @ts-expect-error TS2551 non-existing className */}
                <div className={styles.stepContentCode}>
                  <FormLabel label="TTL" />
                  <div className={styles.stepContentCodeInput}>
                    <Input
                      readOnly
                      value={String(item[1])}
                      ownProps={{ noErrors: true }}
                    />
                    <CopyButton value={String(item[1])} />
                  </div>
                </div>
              </div>
            );
          })}
          <ol className={styles.stepContentList} start={3}>
            <li className={styles.stepContentLastListItem}>
              Once done, refresh verification status by clicking the button
              below.
            </li>
          </ol>
        </div>
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <div className={styles.stepVideo}></div>
      </div>
      <div className={styles.stepContentFooter}>
        <div />
        <ButtonWithSpinner
          onClick={handleContinue}
          disabled={isPolling}
          withSpinner={isPolling}
        >
          Continue
        </ButtonWithSpinner>
      </div>
    </div>
  );
};

export default Verification;
